import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, collection, query, where, writeBatch, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ProfileHeader } from '../components/profile/ProfileHeader';
import { ProfilePicture } from '../components/profile/ProfilePicture';
import { ExternalPhotos } from '../components/profile/ExternalPhotos';
import { useProfileSettings } from '../hooks/useProfileSettings';
import { useAuth } from '../hooks/useAuth';
import { updateUsernameWithHistory } from '../config/firebase';

interface ProfileSettings {
  country: string;
  city: string;
  phonePrefix: string;
  mobileMoneyAccounts: Array<{
    type: 'Orange Money' | 'MTN Mobile Money';
    number: string;
  }>;
  showExternalPhotos: boolean;
  externalPhotos: Array<{
    id: string;
    url: string;
    title: string;
    description: string;
  }>;
}

export function ProfileSettings() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { settings, updateSettings, saveSettings, isLoaded } = useProfileSettings();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading || !isLoaded) {
    return <div className="min-h-screen flex items-center justify-center">
      <p className="text-white">Chargement...</p>
    </div>;
  }

  const handleProfilePictureChange = (croppedImage: string) => {
    // Sauvegarder dans le state
    updateSettings({ profilePicture: croppedImage });
    // Sauvegarder dans localStorage
    if (user?.id) {
      localStorage.setItem(`user_${user.id}_profilePicture`, croppedImage);
    }
  };

  const handleAddSocialLink = () => {
    updateSettings({
      socialLinks: [...settings.socialLinks, { platform: '', url: '' }]
    });
  };

  const handleRemoveSocialLink = (index: number) => {
    updateSettings({
      socialLinks: settings.socialLinks.filter((_, i) => i !== index)
    });
  };

  const handleUpdateSocialLink = (index: number, field: 'platform' | 'url', value: string) => {
    const updatedLinks = [...settings.socialLinks];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    updateSettings({ socialLinks: updatedLinks });
  };

  const handleAddExternalPhoto = (file: File) => {
    // Convertir le fichier en base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      updateSettings({
        externalPhotos: [...settings.externalPhotos, {
          id: Date.now().toString(),
          url: base64String,
          title: '',
          description: ''
        }]
      });

      // Sauvegarder immédiatement dans le localStorage
      if (user?.id) {
        const currentPhotos = JSON.parse(localStorage.getItem(`user_${user.id}_photos`) || '{"externalPhotos": []}');
        currentPhotos.externalPhotos = [...currentPhotos.externalPhotos, {
          id: Date.now().toString(),
          url: base64String,
          title: '',
          description: ''
        }];
        localStorage.setItem(`user_${user.id}_photos`, JSON.stringify(currentPhotos));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveExternalPhoto = (id: string) => {
    updateSettings({
      externalPhotos: settings.externalPhotos.filter(photo => photo.id !== id)
    });
  };

  const handleUpdateExternalPhoto = (id: string, field: 'title' | 'description', value: string) => {
    updateSettings({
      externalPhotos: settings.externalPhotos.map(photo => 
        photo.id === id ? { ...photo, [field]: value } : photo
      )
    });
  };

  const handleToggleShowExternalPhotos = () => {
    updateSettings({ showExternalPhotos: !settings.showExternalPhotos });
    
    // Mettre à jour immédiatement le localStorage pour éviter les problèmes de synchronisation
    if (user?.id) {
      localStorage.setItem(`user_${user.id}_photos`, JSON.stringify({
        showExternalPhotos: !settings.showExternalPhotos,
        externalPhotos: settings.externalPhotos
      }));
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!user?.id) return;

    try {
      // 1. Mettre à jour le state local
      updateSettings({ profilePicture: '' });
      
      // 2. Supprimer du localStorage
      localStorage.removeItem(`user_${user.id}_profilePicture`);
      
      // 3. Mettre à jour Firestore user
      await setDoc(doc(db, 'users', user.id), {
        profilePicture: '',
        hasProfilePicture: false
      }, { merge: true });

      // 4. Mettre à jour toutes les demandes de l'utilisateur
      const requestsRef = collection(db, '2dr_requests');
      const q = query(requestsRef, where('userId', '==', user.id));
      const querySnapshot = await getDocs(q);
      
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, {
          creatorProfilePicture: ''
        });
      });
      await batch.commit();

      // 5. Forcer un rafraîchissement
      window.location.reload();
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
    }
  };

  const handleSave = async () => {
    try {
      const success = await saveSettings();
      if (success && user?.id) {
        if (settings.username !== user.username) {
          await updateUsernameWithHistory(user.id, settings.username);
        }

        // Sauvegarder les photos dans le localStorage
        localStorage.setItem(`user_${user.id}_photos`, JSON.stringify({
          showExternalPhotos: settings.showExternalPhotos,
          externalPhotos: settings.externalPhotos
        }));

        // Sauvegarder l'URL de la photo de profil dans Firestore
        await setDoc(doc(db, 'users', user.id), {
          description: settings.description,
          country: settings.country,
          city: settings.city,
          showExternalPhotos: settings.showExternalPhotos,
          hasProfilePicture: !!settings.profilePicture // Indicateur de présence d'une photo
        }, { merge: true });

        navigate('/profil');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  return (
    <div className="min-h-screen">
      <ProfileHeader onSave={handleSave} />

      <div className="px-1 py-1 space-y-3">
        {/* Avatar temporairement masqué
        <div className="relative">
          <ProfilePicture 
            username={settings.username}
          />
        </div>
        */}

        <div>
          <label className="block text-gray-400 text-sm mb-1">Nom</label>
          <input
            type="text"
            value={settings.username || ''}
            onChange={(e) => updateSettings({ username: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-green-500/50"
          />
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-1">Bio</label>
          <textarea
            value={settings.description}
            onChange={(e) => updateSettings({ description: e.target.value })}
            placeholder="Parlez-nous de vous..."
            maxLength={150}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-green-500/50 resize-none"
            rows={2}
          />
          <p className="text-gray-400 text-xs text-right mt-1">
            {settings.description.length}/150
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-gray-400 text-sm mb-1">Pays</label>
            <input
              type="text"
              value={settings.country}
              onChange={(e) => updateSettings({ country: e.target.value })}
              placeholder="Votre pays"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-green-500/50"
            />
          </div>
          
          <div>
            <label className="block text-gray-400 text-sm mb-1">Ville</label>
            <input
              type="text"
              value={settings.city}
              onChange={(e) => updateSettings({ city: e.target.value })}
              placeholder="Votre ville"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-green-500/50"
            />
          </div>
        </div>

        <div className="space-y-3">
          <label className="block text-gray-400 text-sm">Réseaux sociaux</label>
          {settings.socialLinks.map((link, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center">
                <input
                  type="text"
                  value={link.platform}
                  onChange={(e) => handleUpdateSocialLink(index, 'platform', e.target.value)}
                  placeholder="Nom du réseau social"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-green-500/50"
                />
                <button
                  onClick={() => handleRemoveSocialLink(index)}
                  className="px-3 py-2 text-gray-400 hover:text-white transition-colors ml-2"
                >
                  ×
                </button>
              </div>
              <input
                type="text"
                value={link.url}
                onChange={(e) => handleUpdateSocialLink(index, 'url', e.target.value)}
                placeholder="Lien du profil"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-green-500/50"
              />
            </div>
          ))}
          <button
            onClick={handleAddSocialLink}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-gray-400 hover:text-white transition-colors"
          >
            + Ajouter un réseau social
          </button>
        </div>

        {/* Composant ExternalPhotos temporairement masqué
        <ExternalPhotos
          photos={settings.externalPhotos}
          showPhotos={settings.showExternalPhotos}
          onToggleShow={handleToggleShowExternalPhotos}
          onAdd={handleAddExternalPhoto}
          onRemove={handleRemoveExternalPhoto}
          onUpdate={handleUpdateExternalPhoto}
        />
        */}
      </div>
    </div>
  );
}